import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { errorToast, successToast } from "@telluscompany/design-system";
import {
  axiosDeleteData,
  axiosPatchData,
  axiosPostData,
} from "@shared/api/clients";
import { useLocalization } from "@i18n";

import {
  ADMIN_STRUCTURES_QUERY_KEY,
} from "./structures.queries";
import type {
  AdminStructureDamAnmClassification,
  AdminStructureDamRegionalClassification,
  AdminStructureDetail,
  PatchAdminStructureAnmClassificationRequest,
  PatchAdminStructureRegionalClassificationRequest,
  PatchAdminStructureGeneralDataRequest,
  PatchAdminStructureGeometryRequest,
  PatchAdminStructureHydrologyRequest,
  PatchAdminStructureRiskMappingRequest,
  AdminStructureRaisingHistoryListItem,
  AdminStructureRaisingHistoryPayload,
  AdminStructureReservoirManagementListItem,
  AdminStructureReservoirManagementPayload,
  UploadAdminStructureOrthophotoResponse,
} from "./structures.types";

export const patchAdminStructureGeneralData = async (
  complexId: string,
  structureId: string,
  payload: PatchAdminStructureGeneralDataRequest,
): Promise<AdminStructureDetail> =>
  axiosPatchData<AdminStructureDetail, PatchAdminStructureGeneralDataRequest>(
    `v1/admin/complexes/${complexId}/structures/${structureId}`,
    payload,
  );

export const patchAdminStructureGeometry = async (
  complexId: string,
  structureId: string,
  payload: PatchAdminStructureGeometryRequest,
): Promise<AdminStructureDetail> =>
  axiosPatchData<AdminStructureDetail, PatchAdminStructureGeometryRequest>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/geometry`,
    payload,
  );

export const patchAdminStructureHydrology = async (
  complexId: string,
  structureId: string,
  payload: PatchAdminStructureHydrologyRequest,
): Promise<AdminStructureDetail> =>
  axiosPatchData<AdminStructureDetail, PatchAdminStructureHydrologyRequest>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/hydrology`,
    payload,
  );

export const patchAdminStructureRiskMapping = async (
  complexId: string,
  structureId: string,
  payload: PatchAdminStructureRiskMappingRequest,
): Promise<AdminStructureDetail> =>
  axiosPatchData<AdminStructureDetail, PatchAdminStructureRiskMappingRequest>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/risk-mapping`,
    payload,
  );

export const uploadAdminStructureOrthophoto = async (
  complexId: string,
  structureId: string,
  file: File,
): Promise<UploadAdminStructureOrthophotoResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  return axiosPostData<UploadAdminStructureOrthophotoResponse, FormData>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/orthophoto`,
    formData,
  );
};

export const usePatchAdminStructureGeneralDataMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      payload: PatchAdminStructureGeneralDataRequest;
    }) => patchAdminStructureGeneralData(complexId, structureId, payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          ...ADMIN_STRUCTURES_QUERY_KEY,
          variables.complexId,
          variables.structureId,
          "detail",
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [...ADMIN_STRUCTURES_QUERY_KEY, variables.complexId],
      });
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const usePatchAdminStructureGeometryMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      payload: PatchAdminStructureGeometryRequest;
    }) => patchAdminStructureGeometry(complexId, structureId, payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          ...ADMIN_STRUCTURES_QUERY_KEY,
          variables.complexId,
          variables.structureId,
          "detail",
        ],
      });
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const usePatchAdminStructureHydrologyMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      payload: PatchAdminStructureHydrologyRequest;
    }) => patchAdminStructureHydrology(complexId, structureId, payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          ...ADMIN_STRUCTURES_QUERY_KEY,
          variables.complexId,
          variables.structureId,
          "detail",
        ],
      });
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const usePatchAdminStructureRiskMappingMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      payload: PatchAdminStructureRiskMappingRequest;
    }) => patchAdminStructureRiskMapping(complexId, structureId, payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          ...ADMIN_STRUCTURES_QUERY_KEY,
          variables.complexId,
          variables.structureId,
          "detail",
        ],
      });
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const createAdminStructureRaisingHistory = async (
  complexId: string,
  structureId: string,
  payload: AdminStructureRaisingHistoryPayload,
): Promise<AdminStructureRaisingHistoryListItem> =>
  axiosPostData<
    AdminStructureRaisingHistoryListItem,
    AdminStructureRaisingHistoryPayload
  >(
    `v1/admin/complexes/${complexId}/structures/${structureId}/raising-histories`,
    payload,
  );

export const patchAdminStructureRaisingHistory = async (
  complexId: string,
  structureId: string,
  raisingHistoryId: string,
  payload: AdminStructureRaisingHistoryPayload,
): Promise<AdminStructureRaisingHistoryListItem> =>
  axiosPatchData<
    AdminStructureRaisingHistoryListItem,
    AdminStructureRaisingHistoryPayload
  >(
    `v1/admin/complexes/${complexId}/structures/${structureId}/raising-histories/${raisingHistoryId}`,
    payload,
  );

export const deleteAdminStructureRaisingHistory = async (
  complexId: string,
  structureId: string,
  raisingHistoryId: string,
): Promise<void> =>
  axiosDeleteData<void>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/raising-histories/${raisingHistoryId}`,
  );

const raisingHistoriesQueryKey = (complexId: string, structureId: string) =>
  [...ADMIN_STRUCTURES_QUERY_KEY, complexId, structureId, "raising-histories"] as const;

export const useCreateAdminStructureRaisingHistoryMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      payload: AdminStructureRaisingHistoryPayload;
    }) => createAdminStructureRaisingHistory(complexId, structureId, payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: raisingHistoriesQueryKey(
          variables.complexId,
          variables.structureId,
        ),
      });
      successToast({ description: translate("common.createSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const usePatchAdminStructureRaisingHistoryMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      raisingHistoryId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      raisingHistoryId: string;
      payload: AdminStructureRaisingHistoryPayload;
    }) =>
      patchAdminStructureRaisingHistory(
        complexId,
        structureId,
        raisingHistoryId,
        payload,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: raisingHistoriesQueryKey(
          variables.complexId,
          variables.structureId,
        ),
      });
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const useDeleteAdminStructureRaisingHistoryMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      raisingHistoryId,
    }: {
      complexId: string;
      structureId: string;
      raisingHistoryId: string;
    }) =>
      deleteAdminStructureRaisingHistory(
        complexId,
        structureId,
        raisingHistoryId,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: raisingHistoriesQueryKey(
          variables.complexId,
          variables.structureId,
        ),
      });
      successToast({ description: translate("common.deleteSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};

export const createAdminStructureReservoirManagement = async (
  complexId: string,
  structureId: string,
  payload: AdminStructureReservoirManagementPayload,
): Promise<AdminStructureReservoirManagementListItem> =>
  axiosPostData<
    AdminStructureReservoirManagementListItem,
    AdminStructureReservoirManagementPayload
  >(
    `v1/admin/complexes/${complexId}/structures/${structureId}/reservoir-managements`,
    payload,
  );

export const patchAdminStructureReservoirManagement = async (
  complexId: string,
  structureId: string,
  reservoirManagementId: string,
  payload: AdminStructureReservoirManagementPayload,
): Promise<AdminStructureReservoirManagementListItem> =>
  axiosPatchData<
    AdminStructureReservoirManagementListItem,
    AdminStructureReservoirManagementPayload
  >(
    `v1/admin/complexes/${complexId}/structures/${structureId}/reservoir-managements/${reservoirManagementId}`,
    payload,
  );

export const deleteAdminStructureReservoirManagement = async (
  complexId: string,
  structureId: string,
  reservoirManagementId: string,
): Promise<void> =>
  axiosDeleteData<void>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/reservoir-managements/${reservoirManagementId}`,
  );

const reservoirManagementsQueryKey = (complexId: string, structureId: string) =>
  [
    ...ADMIN_STRUCTURES_QUERY_KEY,
    complexId,
    structureId,
    "reservoir-managements",
  ] as const;

export const useCreateAdminStructureReservoirManagementMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      payload: AdminStructureReservoirManagementPayload;
    }) => createAdminStructureReservoirManagement(complexId, structureId, payload),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reservoirManagementsQueryKey(
          variables.complexId,
          variables.structureId,
        ),
      });
      successToast({ description: translate("common.createSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const usePatchAdminStructureReservoirManagementMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      reservoirManagementId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      reservoirManagementId: string;
      payload: AdminStructureReservoirManagementPayload;
    }) =>
      patchAdminStructureReservoirManagement(
        complexId,
        structureId,
        reservoirManagementId,
        payload,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reservoirManagementsQueryKey(
          variables.complexId,
          variables.structureId,
        ),
      });
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const useDeleteAdminStructureReservoirManagementMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      reservoirManagementId,
    }: {
      complexId: string;
      structureId: string;
      reservoirManagementId: string;
    }) =>
      deleteAdminStructureReservoirManagement(
        complexId,
        structureId,
        reservoirManagementId,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reservoirManagementsQueryKey(
          variables.complexId,
          variables.structureId,
        ),
      });
      successToast({ description: translate("common.deleteSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};

export const patchAdminStructureAnmClassification = async (
  complexId: string,
  structureId: string,
  payload: PatchAdminStructureAnmClassificationRequest,
): Promise<AdminStructureDamAnmClassification> =>
  axiosPatchData<AdminStructureDamAnmClassification, PatchAdminStructureAnmClassificationRequest>(
    `v1/admin/complexes/${complexId}/structures/${structureId}/anm-classification`,
    payload,
  );

const anmClassificationQueryKey = (complexId: string, structureId: string) =>
  [
    ...ADMIN_STRUCTURES_QUERY_KEY,
    complexId,
    structureId,
    "anm-classification",
  ] as const;

export const usePatchAdminStructureAnmClassificationMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      payload: PatchAdminStructureAnmClassificationRequest;
    }) => patchAdminStructureAnmClassification(complexId, structureId, payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        anmClassificationQueryKey(variables.complexId, variables.structureId),
        data,
      );
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const patchAdminStructureRegionalClassification = async (
  complexId: string,
  structureId: string,
  payload: PatchAdminStructureRegionalClassificationRequest,
): Promise<AdminStructureDamRegionalClassification> =>
  axiosPatchData<
    AdminStructureDamRegionalClassification,
    PatchAdminStructureRegionalClassificationRequest
  >(
    `v1/admin/complexes/${complexId}/structures/${structureId}/regional-classification`,
    payload,
  );

const regionalClassificationQueryKey = (
  complexId: string,
  structureId: string,
) =>
  [
    ...ADMIN_STRUCTURES_QUERY_KEY,
    complexId,
    structureId,
    "regional-classification",
  ] as const;

export const usePatchAdminStructureRegionalClassificationMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      payload,
    }: {
      complexId: string;
      structureId: string;
      payload: PatchAdminStructureRegionalClassificationRequest;
    }) =>
      patchAdminStructureRegionalClassification(complexId, structureId, payload),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        regionalClassificationQueryKey(
          variables.complexId,
          variables.structureId,
        ),
        data,
      );
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const useUploadAdminStructureOrthophotoMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      structureId,
      file,
    }: {
      complexId: string;
      structureId: string;
      file: File;
    }) => uploadAdminStructureOrthophoto(complexId, structureId, file),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [
          ...ADMIN_STRUCTURES_QUERY_KEY,
          variables.complexId,
          variables.structureId,
          "detail",
        ],
      });
      successToast({ description: translate("common.editSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description:
          error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};
